import React from 'react';

export class FurnitureRenderer {
  constructor(ctx, scale = 1) {
    this.ctx = ctx;
    this.scale = scale;
  }

  updateScale(scale) {
    this.scale = scale;
  }

  renderItem(item, x, y, rotation = 0, isSelected = false, opacity = 1) {
    // Safety check
    if (!item || !item.render2D) return;

    this.ctx.save();
    this.ctx.translate(x, y);
    this.ctx.rotate(rotation);
    
    // ✅ Obsługa niezależnego skalowania X/Y oraz odbicia lustrzanego
    const baseScaleX = item.scaleX !== undefined ? item.scaleX : (item.scale || 1);
    const baseScaleY = item.scaleY !== undefined ? item.scaleY : (item.scale || 1);

    const scaleX = baseScaleX * (item.flipX ? -1 : 1);
    const scaleY = baseScaleY * (item.flipY ? -1 : 1);
    
    this.ctx.scale(scaleX, scaleY);

    if (isSelected) {
      this.ctx.shadowColor = '#1E2937';
      this.ctx.shadowBlur = 10;
    }

    this.ctx.globalAlpha = opacity;

    // Set base styles based on instance properties or template
    const fillColor = item.fillColor || item.render2D.fill || '#ffffff';
    const strokeColor = item.strokeColor || item.render2D.stroke || '#000000';
    
    this.ctx.fillStyle = fillColor;
    this.ctx.strokeStyle = strokeColor;
    
    // ✅ Obsługa dynamicznej grubości obrysu
    const baseStrokeWidth = item.strokeWidth !== undefined ? item.strokeWidth : (item.render2D.strokeWidth || 0.5);
    const avgScale = (Math.abs(baseScaleX) + Math.abs(baseScaleY)) / 2;
    this.ctx.lineWidth = baseStrokeWidth / (this.scale * avgScale);

    switch (item.render2D.type) {
      case 'rect': this.renderRect(item, fillColor, strokeColor); break;
      case 'circle': this.renderCircle(item, fillColor, strokeColor); break;
      case 'path': this.renderPath(item, fillColor, strokeColor); break;
      case 'group': this.renderGroup(item, fillColor, strokeColor); break;
      default: this.renderRect(item, fillColor, strokeColor);
    }

    if (isSelected) {
      this.ctx.strokeStyle = '#1E2937';
      this.ctx.lineWidth = 3 / (this.scale * avgScale);
      this.ctx.strokeRect(
        -item.dimensions.width / 2 - 5,
        -item.dimensions.depth / 2 - 5,
        item.dimensions.width + 10,
        item.dimensions.depth + 10
      );
    }

    this.ctx.restore();
  }

  renderRect(item, fill, stroke) {
    const { width, depth } = item.dimensions;
    const rectWidth = width;
    const rectHeight = depth;
    
    if (fill !== 'none') {
      this.ctx.fillStyle = fill;
      this.ctx.fillRect(-rectWidth / 2, -rectHeight / 2, rectWidth, rectHeight);
    }
    
    if (stroke !== 'none') {
      this.ctx.strokeStyle = stroke;
      if (item.render2D.strokeDash) {
        const avgScale = (Math.abs(item.scaleX || item.scale || 1) + Math.abs(item.scaleY || item.scale || 1)) / 2;
        this.ctx.setLineDash(item.render2D.strokeDash.map(d => d / (this.scale * avgScale)));
      } else {
        this.ctx.setLineDash([]);
      }
      this.ctx.strokeRect(-rectWidth / 2, -rectHeight / 2, rectWidth, rectHeight);
    }
  }

  renderCircle(item, fill, stroke) {
    const { width, depth } = item.dimensions;
    const radius = Math.min(width, depth) / 2;
    
    this.ctx.beginPath();
    this.ctx.arc(0, 0, radius, 0, Math.PI * 2);
    
    if (fill !== 'none') {
      this.ctx.fillStyle = fill;
      this.ctx.fill();
    }
    if (stroke !== 'none') {
      this.ctx.strokeStyle = stroke;
      this.ctx.stroke();
    }
  }

  renderPath(item, fill, stroke) {
    const { path } = item.render2D;
    this.ctx.beginPath();
    if (path) {
      const commands = parseSVGPath(path);
      commands.forEach(cmd => {
        const cx = item.dimensions.width / 2;
        const cy = item.dimensions.depth / 2;
        switch (cmd.type) {
          case 'M': this.ctx.moveTo(cmd.x - cx, cmd.y - cy); break;
          case 'L': this.ctx.lineTo(cmd.x - cx, cmd.y - cy); break;
          case 'Q': this.ctx.quadraticCurveTo(cmd.x1 - cx, cmd.y1 - cy, cmd.x - cx, cmd.y - cy); break;
          case 'Z': this.ctx.closePath(); break;
        }
      });
    }
    
    if (fill !== 'none') {
      this.ctx.fillStyle = fill;
      this.ctx.fill();
    }
    if (stroke !== 'none') {
      this.ctx.strokeStyle = stroke;
      this.ctx.stroke();
    }
  }

  renderGroup(item, defaultFill, defaultStroke) {
    const { elements } = item.render2D;
    if (!elements) return;
    
    const avgScale = (Math.abs(item.scaleX || item.scale || 1) + Math.abs(item.scaleY || item.scale || 1)) / 2;
    // ✅ Wyznaczona grubość obrysu dla całej grupy, jeśli element ma nadpisaną
    const groupStrokeWidth = item.strokeWidth !== undefined ? item.strokeWidth : null;

    elements.forEach(el => {
      this.ctx.save();
      
      let elFill = el.fill || defaultFill;
      if (el.fill === 'none') elFill = 'none';
      else if (item.fillColor) elFill = item.fillColor; 

      let elStroke = el.stroke || defaultStroke;
      if (el.stroke === 'none') elStroke = 'none';
      else if (item.strokeColor) elStroke = item.strokeColor; 

      // ✅ Używamy nadpisanej grubości lub tej z definicji elementu
      const finalStrokeWidth = groupStrokeWidth !== null ? groupStrokeWidth : (el.strokeWidth || 0.5);
      this.ctx.lineWidth = finalStrokeWidth / (this.scale * avgScale);
      this.ctx.fillStyle = elFill;
      this.ctx.strokeStyle = elStroke;

      if (el.type === 'rect') {
        if (elFill !== 'none') this.ctx.fillRect(el.x, el.y, el.width, el.height);
        if (elStroke !== 'none') this.ctx.strokeRect(el.x, el.y, el.width, el.height);
      } else if (el.type === 'circle') {
        this.ctx.beginPath();
        this.ctx.arc(el.x, el.y, el.radius, 0, Math.PI * 2);
        if (elFill !== 'none') this.ctx.fill();
        if (elStroke !== 'none') this.ctx.stroke();
      } else if (el.type === 'path') {
        this.ctx.beginPath();
        const commands = parseSVGPath(el.path);
        commands.forEach(cmd => {
          switch (cmd.type) {
            case 'M': this.ctx.moveTo(cmd.x, cmd.y); break;
            case 'L': this.ctx.lineTo(cmd.x, cmd.y); break;
            case 'Q': this.ctx.quadraticCurveTo(cmd.x1, cmd.y1, cmd.x, cmd.y); break;
            case 'Z': this.ctx.closePath(); break;
          }
        });
        if (elFill !== 'none') this.ctx.fill();
        if (elStroke !== 'none') this.ctx.stroke();
      }
      this.ctx.restore();
    });
  }
}

function parseSVGPath(pathString) {
  const commands = [];
  if (!pathString) return commands;
  const parts = pathString.match(/[MLZQ][^MLZQ]*/gi);
  if (parts) {
    parts.forEach(part => {
      const type = part[0];
      const coords = part.slice(1).trim().split(/[\s,]+/);
      if (type === 'M' || type === 'L') {
        const x = parseFloat(coords[0]);
        const y = parseFloat(coords[1]);
        commands.push({ type, x, y });
      } else if (type === 'Q') {
        const x1 = parseFloat(coords[0]);
        const y1 = parseFloat(coords[1]);
        const x = parseFloat(coords[2]);
        const y = parseFloat(coords[3]);
        commands.push({ type, x1, y1, x, y });
      } else if (type === 'Z') {
        commands.push({ type: 'Z' });
      }
    });
  }
  return commands;
}

export default FurnitureRenderer;